import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Check, ChevronsUpDown, List, Loader2, Lock, Save } from "lucide-react";
import { PageContainer, PageHeader, Card, SectionTitle } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/hooks/useCurrency";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import ImageUploadField from "@/components/common/ImageUploadField";
import {
  EMPTY_FORM,
  itemToForm,
  formToPayload,
  previewNextMenuCode,
  apiError,
  validateForm,
  num,
  roundMoney,
  withVat,
  withoutVat,
} from "./menuItemFormUtils";

const EMPTY_LIST = [];

function selectValue(value) {
  return value ? String(value) : undefined;
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function Flag({ checked, onChange, label, testId }) {
  return (
    <label
      className={cn(
        "flex items-center gap-2 h-10 rounded-lg border px-3 text-[12px] font-medium cursor-pointer min-w-0",
        checked
          ? "border-brand-orange/40 bg-[#FFF3E8] text-brand-brown"
          : "border-[#E8D5C8] bg-white text-slate-600 hover:bg-[#FFF8F3]",
      )}
    >
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} data-testid={testId} />
      <span className="truncate">{label}</span>
    </label>
  );
}

function SearchableSubMenuSelect({ options, value, onChange, placeholder = "Select subcategory", disabled = false }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((s) => String(s.id) === String(value));

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-9 w-full justify-between font-normal px-3"
          data-testid="menu-item-sub-menu"
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>
            {selected ? (
              <span className="flex items-center gap-1.5 truncate">
                <span className="truncate">{selected.name}</span>
                {selected.main ? (
                  <span className="text-[11px] text-muted-foreground font-normal">
                    ({selected.main})
                  </span>
                ) : null}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search subcategory or category…" />
          <CommandList className="max-h-64 overflow-y-auto">
            <CommandEmpty>No subcategory found.</CommandEmpty>
            <CommandGroup>
              {options.map((s) => {
                const id = String(s.id);
                const label = s.name;
                const isSelected = String(value) === id;
                return (
                  <CommandItem
                    key={id}
                    value={`${label} ${s.main || ""}`}
                    onSelect={() => {
                      onChange(id);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between py-2 px-2.5 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          isSelected ? "opacity-100 text-brand-orange" : "opacity-0",
                        )}
                      />
                      <span className={cn("truncate", isSelected && "font-semibold")}>{label}</span>
                    </div>
                    {s.main && (
                      <span className="ml-2 shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-normal">
                        {s.main}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function MenuItemFormPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { code: currencyCode, step: moneyInputStep, decimals } = useCurrency();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ["menu-items", branchId],
    queryFn: async () => {
      const { data } = await api.get("/menu-items", { params: { branch_id: branchId } });
      return data.menu_items;
    },
    enabled: Boolean(branchId),
  });
  const items = itemsData ?? EMPTY_LIST;
  const nextCode = useMemo(() => previewNextMenuCode(items), [items]);

  const editingItem = useMemo(
    () => (isNew ? null : items.find((m) => String(m.id) === String(id))),
    [items, id, isNew],
  );

  const { data: mainMenusData } = useQuery({
    queryKey: ["main-menus", branchId],
    queryFn: async () => {
      const { data } = await api.get("/main-menus", { params: { branch_id: branchId } });
      return data.main_menus;
    },
    enabled: Boolean(branchId),
  });
  const mainMenus = mainMenusData ?? EMPTY_LIST;

  const { data: subMenusData } = useQuery({
    queryKey: ["sub-menus", branchId],
    queryFn: async () => {
      const { data } = await api.get("/sub-menus", { params: { branch_id: branchId } });
      return data.sub_menus;
    },
    enabled: Boolean(branchId),
  });
  const subMenus = subMenusData ?? EMPTY_LIST;

  const subcategoryOptions = useMemo(() => {
    return subMenus
      .filter((s) => s.is_active !== false || String(s.id) === String(form.sub_menu_id))
      .map((s) => ({
        ...s,
        main: s.main || mainMenus.find((m) => String(m.id) === String(s.main_menu_id))?.name || "",
      }));
  }, [subMenus, mainMenus, form.sub_menu_id]);

  const selectedSubMenu = useMemo(
    () => subcategoryOptions.find((s) => String(s.id) === String(form.sub_menu_id))
      || subMenus.find((s) => String(s.id) === String(form.sub_menu_id)),
    [subcategoryOptions, subMenus, form.sub_menu_id],
  );

  const selectedCategoryName = useMemo(() => {
    if (selectedSubMenu?.main) return selectedSubMenu.main;
    const cat = mainMenus.find((m) => String(m.id) === String(form.main_menu_id || selectedSubMenu?.main_menu_id));
    return cat?.name || (editingItem?.main_menu ?? "");
  }, [selectedSubMenu, mainMenus, form.main_menu_id, editingItem]);

  const { data: kotStationsData } = useQuery({
    queryKey: ["kitchen-stations", branchId],
    queryFn: async () => {
      const { data } = await api.get("/kitchen-stations", { params: { branch_id: branchId } });
      return data.stations;
    },
    enabled: Boolean(branchId),
  });
  const kotStations = (kotStationsData ?? EMPTY_LIST).filter((s) => s.is_active !== false);

  const hydratedKey = useRef("");

  useEffect(() => {
    if (!isNew) {
      if (!editingItem) return;
      // Wait for subcategory masters so category can be synced from DB relation.
      if (editingItem.sub_menu_id && subMenus.length === 0) return;
      const key = `edit-${editingItem.id}`;
      if (hydratedKey.current === key) return;
      hydratedKey.current = key;
      const next = itemToForm(editingItem, decimals);
      const sub = subMenus.find((s) => String(s.id) === String(next.sub_menu_id));
      if (sub?.main_menu_id) {
        next.main_menu_id = String(sub.main_menu_id);
      }
      setForm(next);
      return;
    }
    if (hydratedKey.current === "new") return;
    hydratedKey.current = "new";
    setForm((prev) => ({
      ...prev,
      kitchen_station_id: prev.kitchen_station_id || (kotStations[0] ? String(kotStations[0].id) : ""),
    }));
  }, [isNew, editingItem, subMenus, kotStations, decimals]);

  useEffect(() => {
    if (!isNew || form.kitchen_station_id || !kotStations[0]) return;
    setForm((prev) => (
      prev.kitchen_station_id ? prev : { ...prev, kitchen_station_id: String(kotStations[0].id) }
    ));
  }, [isNew, kotStations, form.kitchen_station_id]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload(form);
      if (!payload.main_menu_id && form.sub_menu_id) {
        const sub = subMenus.find((s) => String(s.id) === String(form.sub_menu_id));
        if (sub?.main_menu_id) {
          payload.main_menu_id = Number(sub.main_menu_id);
        }
      }
      if (isNew) {
        return api.post("/menu-items", payload, { params: { branch_id: branchId } });
      }
      return api.put(`/menu-items/${id}`, payload, { params: { branch_id: branchId } });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["menu-items", branchId] });
      toast.success(res.data.message);
      navigate("/masters/menu-item/list");
    },
    onError: (err) => toast.error(apiError(err, "Failed to save menu item")),
  });

  function setField(key, value) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "sub_menu_id") {
        const sub = subMenus.find((s) => String(s.id) === String(value));
        next.main_menu_id = sub?.main_menu_id ? String(sub.main_menu_id) : "";
      }
      return next;
    });
  }

  function patch(updates) {
    setForm((prev) => ({ ...prev, ...updates }));
  }

  function setCostEx(value) {
    const vat = num(form.vat_percent);
    if (value === "") {
      patch({ cost_ex_vat: "", cost_inc_vat: "" });
      return;
    }
    patch({
      cost_ex_vat: value,
      cost_inc_vat: roundMoney(withVat(value, vat), decimals),
    });
  }

  function setCostInc(value) {
    const vat = num(form.vat_percent);
    if (value === "") {
      patch({ cost_inc_vat: "", cost_ex_vat: "" });
      return;
    }
    patch({
      cost_inc_vat: value,
      cost_ex_vat: roundMoney(withoutVat(value, vat), decimals),
    });
  }

  function setRetailEx(value) {
    const vat = num(form.vat_percent);
    const clearing = value === "";
    patch({
      retail_ex: value,
      price: clearing ? "" : roundMoney(withVat(value, vat), decimals),
      // Single retail and size variants are mutually exclusive.
      ...(clearing ? {} : { small_price: "", medium_price: "", large_price: "" }),
    });
  }

  function setRetailInc(value) {
    const vat = num(form.vat_percent);
    const clearing = value === "";
    patch({
      price: value,
      retail_ex: clearing ? "" : roundMoney(withoutVat(value, vat), decimals),
      ...(clearing ? {} : { small_price: "", medium_price: "", large_price: "" }),
    });
  }

  function setSizePrice(key, value) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      const hasAnySize = [next.small_price, next.medium_price, next.large_price]
        .some((v) => v !== "" && v !== null && v !== undefined);
      if (hasAnySize) {
        next.price = "";
        next.retail_ex = "";
      }
      return next;
    });
  }

  function setVat(value) {
    const vat = num(value);
    const next = { vat_percent: value };
    if (form.retail_ex !== "") {
      next.price = roundMoney(withVat(form.retail_ex, vat), decimals);
    } else if (form.price !== "") {
      next.retail_ex = roundMoney(withoutVat(form.price, vat), decimals);
    }
    if (form.cost_ex_vat !== "") {
      next.cost_inc_vat = roundMoney(withVat(form.cost_ex_vat, vat), decimals);
    } else if (form.cost_inc_vat !== "") {
      next.cost_ex_vat = roundMoney(withoutVat(form.cost_inc_vat, vat), decimals);
    }
    patch(next);
  }

  function handleSave() {
    const err = validateForm(form);
    if (err) {
      toast.error(err);
      return;
    }
    saveMutation.mutate();
  }

  if (!isNew && itemsLoading) {
    return (
      <PageContainer>
        <div className="h-40 grid place-items-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageContainer>
    );
  }

  if (!isNew && !itemsLoading && !editingItem) {
    return (
      <PageContainer>
        <PageHeader title="Menu item not found" />
        <Button variant="outline" onClick={() => navigate("/masters/menu-item/list")}>
          View list
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={isNew ? "Add menu item" : `Edit ${form.name || "item"}`}
        subtitle={isNew ? "Create a product for POS and kitchen" : form.code}
        actions={
          <>
            <Button variant="outline" size="sm" className="h-9" onClick={() => navigate("/masters/menu-item/list")} data-testid="menu-item-cancel">
              <List size={14} className="mr-1.5" />
              View list
            </Button>
            <Button
              size="sm"
              className="h-9 bg-[#16A34A] hover:bg-emerald-700"
              onClick={handleSave}
              disabled={saveMutation.isPending}
              data-testid="menu-item-save"
            >
              {saveMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Save
            </Button>
          </>
        }
      />

      <Card className="space-y-6">
        <section>
          <SectionTitle>Category &amp; identity</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
              <Field label="Subcategory *">
                <SearchableSubMenuSelect
                  options={subcategoryOptions}
                  value={form.sub_menu_id}
                  onChange={(v) => setField("sub_menu_id", v)}
                  placeholder="Select subcategory"
                />
              </Field>
              <Field label="Category">
                <div className="relative">
                  <Input
                    value={selectedCategoryName}
                    readOnly
                    tabIndex={-1}
                    placeholder="Auto-assigned from subcategory"
                    className="h-9 bg-slate-100 text-slate-700 cursor-not-allowed font-medium pr-8"
                    data-testid="menu-item-main-menu"
                  />
                  <Lock size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </Field>
              <Field label="Menu ID / Code">
                <Input
                  value={isNew ? nextCode : form.code}
                  readOnly
                  className="h-9 font-mono bg-slate-50"
                  data-testid="menu-item-code"
                />
                {isNew ? (
                  <p className="text-[11px] text-slate-500 mt-1">Assigned automatically when you save.</p>
                ) : null}
              </Field>
              <Field label="Item Name *">
                <Input value={form.name} onChange={(e) => setField("name", e.target.value)} className="h-9" data-testid="menu-item-name" />
              </Field>
              <Field label="Arabic name">
                <Input
                  value={form.name_ar}
                  onChange={(e) => setField("name_ar", e.target.value)}
                  className="h-9"
                  dir="rtl"
                  lang="ar"
                  placeholder="الاسم بالعربية"
                  data-testid="menu-item-name-ar"
                />
              </Field>
              <Field label="Unit">
                <Select value={selectValue(form.unit)} onValueChange={(v) => setField("unit", v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Plate", "Piece", "Bowl", "Glass", "Cup", "Combo", "Pot"].map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="KOT">
                <Select
                  value={form.kitchen_station_id || "none"}
                  onValueChange={(v) => setField("kitchen_station_id", v === "none" ? "" : v)}
                >
                  <SelectTrigger className="h-9"><SelectValue placeholder="No KOT" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No KOT</SelectItem>
                    {kotStations.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.code}{s.name && s.name !== s.code ? ` — ${s.name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
          </div>
        </section>

        <section className="border-t pt-5">
          <SectionTitle>Pricing &amp; tax ({currencyCode})</SectionTitle>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 md:grid-cols-3 lg:grid-cols-4">
            <Field label="Cost excl. VAT">
              <Input
                type="number"
                min="0"
                step={moneyInputStep}
                value={form.cost_ex_vat}
                onChange={(e) => setCostEx(e.target.value)}
                className="h-9 font-mono text-right bg-white"
                data-testid="menu-item-cost-ex"
              />
            </Field>
            <Field label="Cost inc. VAT">
              <Input
                type="number"
                min="0"
                step={moneyInputStep}
                value={form.cost_inc_vat}
                onChange={(e) => setCostInc(e.target.value)}
                className="h-9 font-mono text-right bg-white"
                data-testid="menu-item-cost-inc"
              />
            </Field>
            <Field label="VAT %">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.vat_percent}
                onChange={(e) => setVat(e.target.value)}
                className="h-9 text-right bg-white"
                data-testid="menu-item-vat"
              />
            </Field>
            <Field label="Tax inclusive">
              <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-white">
                <Switch checked={form.tax_inclusive} onCheckedChange={(v) => setField("tax_inclusive", v)} />
                <span className="text-[12px] text-slate-700">VAT in sell price</span>
              </div>
            </Field>
            <Field label="Retail exclusive">
              <Input
                type="number"
                min="0"
                step={moneyInputStep}
                value={form.retail_ex}
                onChange={(e) => setRetailEx(e.target.value)}
                className="h-9 font-mono text-right bg-white"
                placeholder="Before VAT"
                data-testid="menu-item-retail-ex"
              />
            </Field>
            <Field label="Retail inclusive">
              <Input
                type="number"
                min="0"
                step={moneyInputStep}
                value={form.price}
                onChange={(e) => setRetailInc(e.target.value)}
                className="h-9 font-mono text-right font-semibold bg-white"
                placeholder="After VAT"
                data-testid="menu-item-price"
              />
            </Field>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            Enter either a single retail price (ex / inc updates from VAT %), or Small / Medium / Large variants below — not both.
          </p>
          <div className="mt-4 rounded-xl border border-[#F0E0D4] bg-[#FFF8F3] p-4 space-y-3">
            <div className="text-[13px] font-semibold text-brand-brown">Size variants</div>
            <p className="text-[11px] text-slate-500">
              Optional. Filling any size clears the single retail price above.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
              <Field label="Small">
                <Input value={form.small_price} onChange={(e) => setSizePrice("small_price", e.target.value)} placeholder="—" className="h-9 font-mono text-right" type="number" min="0" step={moneyInputStep} />
              </Field>
              <Field label="Medium">
                <Input value={form.medium_price} onChange={(e) => setSizePrice("medium_price", e.target.value)} placeholder="—" className="h-9 font-mono text-right" type="number" min="0" step={moneyInputStep} />
              </Field>
              <Field label="Large">
                <Input value={form.large_price} onChange={(e) => setSizePrice("large_price", e.target.value)} placeholder="—" className="h-9 font-mono text-right" type="number" min="0" step={moneyInputStep} />
              </Field>
            </div>
          </div>
        </section>

        <section className="border-t pt-5">
          <SectionTitle>POS, kitchen &amp; status</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2">
            <Select value={selectValue(form.status)} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger className="h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
                <SelectItem value="OutOfStock">Out of Stock</SelectItem>
              </SelectContent>
            </Select>
            <Flag checked={form.show_in_pos} onChange={(v) => setField("show_in_pos", v)} label="Show on POS" />
            <Flag checked={form.show_on_online_catalog} onChange={(v) => setField("show_on_online_catalog", v)} label="Online catalog" testId="online-catalog-checkbox" />
            <Flag checked={form.allow_discount} onChange={(v) => setField("allow_discount", v)} label="Allow discount" />
          </div>
          {form.show_on_online_catalog ? (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-orange-100 bg-orange-50/50 px-3 py-3">
              <Field label="Online display name">
                <Input value={form.online_name} onChange={(e) => setField("online_name", e.target.value)} placeholder="Defaults to item name" className="h-9 bg-white" />
              </Field>
              <Field label="Online description">
                <Input value={form.online_description} onChange={(e) => setField("online_description", e.target.value)} placeholder="Short catalog blurb" className="h-9 bg-white" />
              </Field>
            </div>
          ) : null}
        </section>

        <section className="border-t pt-5">
          <SectionTitle>POS image</SectionTitle>
          <div className="rounded-xl border border-[#E8D5C8] bg-[#FFFCF9] p-4 max-w-2xl">
            <ImageUploadField
              label=""
              hint="Shown on the product grid. Upload a photo or paste an image URL."
              branchId={branchId}
              value={form.image_url}
              onChange={(v) => setField("image_url", v)}
              testId="menu-item-image"
            />
          </div>
        </section>
      </Card>
    </PageContainer>
  );
}
